import React, { useEffect, useState, useCallback, useRef } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import {
    Loader2, Cloud, CheckCircle2, AlertCircle, FolderOpen, RefreshCcw,
    Unlink, ChevronRight, Search, Hourglass, ArrowLeft,
} from "lucide-react";


// Iter 31.159 — _Inbox preview row with inline room-tag picker.
// Phone-default filenames like IMG_10265.jpg never match the strict
// `room-<slug>-*` regex and silently fall through to the default LLM
// shredder. This row gives the operator a one-tap deterministic
// "tag this image as room: kitchen / bathroom / …" before the next
// poll fires, renaming the file in Drive via /api/oauth/drive/inbox/tag-room.
const ROOM_PICKER_CHOICES = [
    "kitchen", "bathroom", "bedroom", "living_room",
    "hallway", "office", "garden", "garage",
];
const ROOM_CANONICAL_RX = /^room-([a-z][a-z0-9_]*)-.+\.(jpg|jpeg|png|webp)$/i;

function InboxFileRow({ file, onTagged }) {
    const [picking, setPicking] = useState(false);
    const [custom, setCustom] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const isImage = (file.mime || "").startsWith("image/");
    const isCanonical = ROOM_CANONICAL_RX.test(file.name || "");
    const tag = async (room) => {
        if (!room || !file.id) return;
        setBusy(true); setErr("");
        try {
            await api.post("/oauth/drive/inbox/tag-room", { file_id: file.id, room });
            setPicking(false);
            setCustom("");
            if (onTagged) await onTagged();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "tag failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <li data-testid={`drive-inbox-row-${file.id || file.name}`} className="border-l-2 border-ink/15 pl-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <code className="text-ink break-all">{file.name}</code>
                    {file.is_google_doc && <span className="text-stamp-amber ml-2">· GOOGLE DOC (auto-exported)</span>}
                    {isImage && !isCanonical && (
                        <span className="text-stamp-red ml-2 text-[10px] font-heading uppercase tracking-[0.18em]">
                            · untagged image
                        </span>
                    )}
                    {isImage && isCanonical && (
                        <span className="text-emerald-700 ml-2 text-[10px] font-heading uppercase tracking-[0.18em]">
                            · room-tagged
                        </span>
                    )}
                </div>
                {isImage && !isCanonical && (
                    <button
                        type="button"
                        onClick={() => setPicking((v) => !v)}
                        disabled={busy}
                        className="font-heading uppercase tracking-[0.16em] text-[10px] px-2 py-0.5 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-50 transition-colors"
                        data-testid={`drive-inbox-tag-toggle-${file.id}`}
                    >
                        {picking ? "cancel" : "tag room"}
                    </button>
                )}
            </div>
            {picking && (
                <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid={`drive-inbox-picker-${file.id}`}>
                    {ROOM_PICKER_CHOICES.map((r) => (
                        <button
                            key={r}
                            type="button"
                            onClick={() => tag(r)}
                            disabled={busy}
                            className="font-mono text-[10px] px-2 py-0.5 border border-ink/40 hover:border-stamp-red hover:text-stamp-red disabled:opacity-50"
                            data-testid={`drive-inbox-tag-${r}`}
                        >
                            {r}
                        </button>
                    ))}
                    <span className="text-ink-faint text-[10px]">or</span>
                    <input
                        type="text"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        placeholder="custom_slug"
                        className="bg-transparent border border-ink/30 text-[10px] px-2 py-0.5 font-mono w-32"
                        data-testid={`drive-inbox-tag-custom-${file.id}`}
                    />
                    <button
                        type="button"
                        onClick={() => tag(custom)}
                        disabled={busy || !custom.trim()}
                        className="font-heading uppercase tracking-[0.16em] text-[10px] px-2 py-0.5 border border-ink/40 hover:border-stamp-red hover:text-stamp-red disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "apply"}
                    </button>
                </div>
            )}
            {err && (
                <div className="mt-1 text-[10px] text-stamp-red font-body">{err}</div>
            )}
        </li>
    );
}

/**
 * Drive Watch — iter 28 replacement for the in-app Shredder upload UI.
 *
 * Flow:
 *   1. Status — banner showing whether OAuth is configured / user is connected / folder pinned
 *   2. Connect — opens Google OAuth in a new tab; the backend's callback
 *      302s back to /shed?drive_connected=1
 *   3. Pick folder — browse the user's Drive folders, pin one
 *   4. Watching — show last poll result + ingestion log + "poll now" button
 *
 * Polling runs server-side every 5 minutes (drive_watch.py::_poll_loop).
 * The user never uploads anything to our backend over HTTP — files flow
 * through Drive, which sidesteps the entire 504/proxy class of failure.
 */
export default function DriveWatch() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");
    const [polling, setPolling] = useState(false);
    const [folders, setFolders] = useState([]);
    const [folderCrumbs, setFolderCrumbs] = useState([]); // [{id, name}]
    const [folderQuery, setFolderQuery] = useState("");
    const [showPicker, setShowPicker] = useState(false);
    const [ingestLog, setIngestLog] = useState([]);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/oauth/drive/status");
            setStatus(data);
            statusRef.current = data;
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Status failed");
        } finally {
            setBusy(false);
        }
    }, []);

    // Ref mirror of `status` so async loops (poll watcher) can read the
    // latest value without re-renders re-creating the closure.
    const statusRef = useRef(null);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    // If we came back from the OAuth callback (?drive_connected=1) — clear
    // the URL flag and refresh status so the banner updates instantly.
    // iter 28.3: also handle the scope-refusal case (?drive_scope_error=...)
    // — surface a clear "tick every box" panel instead of a silent JSON 400.
    const [scopeError, setScopeError] = useState(null);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has("drive_connected")) {
            window.history.replaceState({}, "", "/shredder");
            loadStatus();
        } else if (params.has("drive_scope_error")) {
            setScopeError({
                kind: params.get("drive_scope_error"),
                missing: params.get("missing") || "",
                granted: params.get("granted") || "",
            });
            window.history.replaceState({}, "", "/shredder");
        }
    }, [loadStatus]);

    const connect = async () => {
        try {
            const { data } = await api.post("/oauth/drive/connect");
            // Open in same tab — Google's consent screen + callback flow.
            window.location.href = data.authorization_url;
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Connect failed");
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Google Drive? Ingested ledger entries stay.")) return;
        // Iter 31.269 — make the disconnect resilient. Previously a silent
        // throw left the button in its existing state with no feedback,
        // which Six saw on 17 Feb 2026 ("the disconnect button won't allow
        // disconnect to connect again even though it's not connected").
        // We now: set a busy flag, surface any error, force-refresh status
        // even on failure (the backend handler is fire-and-forget for the
        // OAuth revoke — if it 500s, the rows may still be gone).
        setBusy(true);
        setErr("");
        try {
            await api.delete("/oauth/drive/disconnect");
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Disconnect failed — try refreshing the page.");
        } finally {
            try {
                await loadStatus();
            } catch (_e) {
                /* loadStatus owns its own setErr — don't double-set */
            }
            setBusy(false);
        }
    };

    const openPicker = async (parentId = null) => {
        setShowPicker(true);
        setFolderQuery("");
        await loadFolders(parentId);
    };

    const loadFolders = async (parentId = null, q = "") => {
        try {
            const params = {};
            if (parentId) params.parent = parentId;
            if (q) params.q = q;
            const { data } = await api.get("/oauth/drive/folders", { params });
            setFolders(data.folders || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Folder list failed");
        }
    };

    const descendInto = async (folder) => {
        setFolderCrumbs([...folderCrumbs, folder]);
        await loadFolders(folder.id);
    };

    const backOneLevel = async () => {
        const next = folderCrumbs.slice(0, -1);
        setFolderCrumbs(next);
        const parent = next.length ? next[next.length - 1].id : null;
        await loadFolders(parent);
    };

    const pinFolder = async (folder) => {
        await api.post("/oauth/drive/folder", { folder_id: folder.id, folder_name: folder.name });
        setShowPicker(false);
        await loadStatus();
        // Run a poll immediately so the user sees something happen.
        await pollNow();
    };

    // iter 31.10 — one-shot folder reorganisation to the type/date schema.
    // Always shows a dry-run summary first, then asks for confirmation
    // before mutating Drive. No file content is altered — only Drive
    // parents move, so the hash-chain stays intact.
    const [reorganising, setReorganising] = useState(false);
    const reorganise = async () => {
        setReorganising(true);
        setErr("");
        try {
            const dry = await api.post("/oauth/drive/reorganise?dry_run=true");
            const s = dry.data.summary || {};
            const lines = [
                `Reorganise to <type>/YYYY-MM-DD schema`,
                ``,
                `Scanned: ${s.total_scanned ?? 0} files`,
                `Will move: ${s.total_moved ?? 0}`,
                `Already in place: ${s.total_skipped ?? 0}`,
                `Errors during dry-run: ${s.total_errors ?? 0}`,
                ``,
                `Proceed with the live move?`,
            ];
            if (!window.confirm(lines.join("\n"))) {
                setReorganising(false);
                return;
            }
            const live = await api.post("/oauth/drive/reorganise?dry_run=false");
            const ls = live.data.summary || {};
            window.alert(`Reorganise complete.\nMoved: ${ls.total_moved}\nSkipped: ${ls.total_skipped}\nErrors: ${ls.total_errors}`);
            await loadStatus();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Reorganise failed");
        } finally {
            setReorganising(false);
        }
    };

    const pollNow = async () => {
        setPolling(true);
        try {
            // iter 30.4: server returns immediately with `{started: true}`
            // and the work runs in background. Refresh status briefly to
            // pick up the new `last_poll_at`/`last_poll_result` whenever
            // the background task finishes (could be 5s, could be 5min).
            await api.post("/oauth/drive/poll");
            // Optimistic single refresh, then poll status every 10s for
            // up to 5min so the UI surfaces the count as it lands.
            await loadStatus();
            const start = Date.now();
            const lastPollSeen = statusRef.current?.last_poll_at || null;
            while (Date.now() - start < 5 * 60 * 1000) {
                await new Promise((r) => setTimeout(r, 10000));
                await loadStatus();
                if (statusRef.current?.last_poll_at && statusRef.current.last_poll_at !== lastPollSeen) {
                    break;
                }
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Poll failed");
        } finally {
            setPolling(false);
        }
    };

    // Iter 31.23 — per-user polling controls. Save and refresh.
    const saveSettings = async (patch) => {
        try {
            await api.post("/oauth/drive/settings", patch);
            await loadStatus();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Settings save failed");
        }
    };

    // ----- render -----
    return (
        <Shell>
            <div className="mb-7">
                <div className="label-typed tw-prefix">One Entrance · Google Drive Watch · Iter 28</div>
                <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                    Shredder<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Connect a Google Drive folder. The Shed auto-creates four sub-folders inside it
                    (<span className="text-stamp-red">_Inbox</span>, _Filed, _Duplicates, _Failed).
                    Drop files into <span className="text-stamp-red">_Inbox</span> from any device.
                    Every 5 minutes the Shed reads them, files renames into
                    <span className="text-stamp-red"> _Filed/&lt;type&gt;/YYYY-MM-DD/</span>, moves
                    duplicates aside, and leaves originals untouched in Drive. No upload. No proxy. No 504.
                </p>
            </div>

            {busy && (
                <div className="paper-card p-12 text-center" data-testid="drive-busy">
                    <Loader2 className="w-7 h-7 text-stamp-red mx-auto mb-3 animate-spin" />
                    <div className="label-typed text-stamp-red">Reading the wires…</div>
                </div>
            )}

            {err && !busy && (
                <div className="paper-card border-2 border-stamp-red p-5 mb-6" data-testid="drive-error">
                    <div className="flex items-center gap-2 stamp-red font-heading text-sm tracking-[0.2em] mb-2">
                        <AlertCircle className="w-4 h-4" /> ERROR
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap">{err}</pre>
                </div>
            )}

            {scopeError && (
                <div
                    className="paper-card border-2 border-stamp-red p-5 mb-6"
                    data-testid="drive-scope-error"
                >
                    <div className="flex items-center gap-2 stamp-red font-heading text-sm tracking-[0.2em] mb-3">
                        <AlertCircle className="w-4 h-4" /> GOOGLE REFUSED THE FULL DRIVE SCOPE
                    </div>
                    <div className="font-body text-sm text-ink leading-relaxed space-y-3">
                        <p>
                            Google did not grant the Drive permission needed to watch a folder.
                            Three possible causes — work through them in order:
                        </p>
                        <ol className="list-decimal pl-5 space-y-1.5 text-xs">
                            <li>
                                <strong>You unticked the Drive box on the consent screen.</strong>
                                {" "}Click <em>Authorise</em> below and on the Google screen make sure
                                <strong> every checkbox</strong> is ticked before continuing.
                            </li>
                            <li>
                                <strong>The OAuth app is in &ldquo;Testing&rdquo; mode without your email
                                listed as a Test User.</strong> Cloud Console &rarr; OAuth consent screen &rarr;
                                Test users &rarr; add the Google account you sign in with.
                            </li>
                            <li>
                                <strong>The app is in &ldquo;In production&rdquo; mode but not yet verified.</strong>
                                {" "}Google silently degrades restricted scopes for unverified apps.
                                Submit for verification (Cloud Console &rarr; OAuth consent screen &rarr;
                                <em>Prepare for verification</em>) — 4-6 weeks, free.
                            </li>
                        </ol>
                        <div className="grid grid-cols-2 gap-3 mt-3 text-[10px] font-body">
                            <div>
                                <div className="label-typed">Requested scope</div>
                                <code className="text-ink break-all">
                                    {scopeError.missing || "https://www.googleapis.com/auth/drive"}
                                </code>
                            </div>
                            <div>
                                <div className="label-typed">Google returned</div>
                                <code className="text-ink-soft break-all">
                                    {scopeError.granted || "(nothing)"}
                                </code>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={() => { setScopeError(null); connect(); }}
                                className="bg-ink text-paper-light font-heading uppercase tracking-[0.2em] text-xs py-2 px-4 hover:bg-stamp-red transition-colors"
                                data-testid="drive-scope-retry"
                            >
                                Authorise again
                            </button>
                            <button
                                onClick={() => setScopeError(null)}
                                className="font-body text-xs text-ink-soft hover:text-stamp-red uppercase tracking-[0.18em]"
                                data-testid="drive-scope-dismiss"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!busy && status && !status.configured && (
                <NotConfiguredCard />
            )}

            {!busy && status?.configured && !status.connected && (
                <ConnectCard onConnect={connect} />
            )}

            {!busy && status?.connected && !status.watched_folder_id && (
                <PickFolderPromptCard
                    email={status.google_email}
                    onPick={() => openPicker()}
                    onDisconnect={disconnect}
                />
            )}

            {!busy && status?.connected && status.watched_folder_id && (
                <WatchingCard
                    status={status}
                    polling={polling}
                    reorganising={reorganising}
                    onPollNow={pollNow}
                    onReorganise={reorganise}
                    onPickAgain={() => openPicker()}
                    onDisconnect={disconnect}
                    onSaveSettings={saveSettings}
                />
            )}

            {showPicker && (
                <FolderPickerModal
                    folders={folders}
                    crumbs={folderCrumbs}
                    query={folderQuery}
                    onQuery={async (q) => {
                        setFolderQuery(q);
                        const parent = folderCrumbs.length ? folderCrumbs[folderCrumbs.length - 1].id : null;
                        await loadFolders(parent, q);
                    }}
                    onDescend={descendInto}
                    onBack={backOneLevel}
                    onPin={pinFolder}
                    onClose={() => setShowPicker(false)}
                />
            )}

            {/* Iter 31.16 (23 May 2026) — Amazon Photos explainer.
                Amazon shut their Drive/Photos API to new developers in 2023
                and revoked existing keys in mid-2024. Workarounds only. */}
            <AmazonPhotosCard />
        </Shell>
    );
}

function AmazonPhotosCard() {
    const [open, setOpen] = useState(false);
    return (
        <div className="paper-card p-5 mt-4" data-testid="amazon-photos-card">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                data-testid="amazon-photos-toggle"
                aria-expanded={open}
            >
                <div>
                    <div className="label-typed text-[10px] tracking-[0.25em] text-ink-soft">
                        Other Sources · Amazon Photos
                    </div>
                    <div className="font-heading text-lg text-ink mt-1">
                        Amazon Photos — workarounds only
                    </div>
                </div>
                <ChevronRight
                    className={`w-4 h-4 text-ink-soft transition-transform ${open ? "rotate-90" : ""}`}
                    strokeWidth={1.6}
                />
            </button>
            {open && (
                <div className="mt-4 font-body text-sm text-ink-soft space-y-3 leading-relaxed border-t border-ink/15 pt-4">
                    <p>
                        Unlike Google Drive, Gmail and OneDrive — Amazon has{" "}
                        <strong className="text-stamp-red">no public developer API</strong> for Amazon Photos.
                        They shut Amazon Drive's API to new developers in 2023 and
                        revoked existing keys mid-2024. No timeline for re-opening.
                    </p>
                    <p>
                        Two reliable ways to get your Amazon Photos into the Shed:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            <strong className="text-ink">Manual export</strong>{" "}
                            (recommended for one-off bulk import).{" "}
                            <a
                                href="https://www.amazon.com/photos"
                                target="_blank"
                                rel="noreferrer"
                                className="text-stamp-red underline hover:no-underline"
                            >
                                amazon.com/photos
                            </a>{" "}
                            → select photos → <em>Download</em> → unzip the
                            archive into your watched Drive folder. The Shed
                            ingests them via this Drive Watch as if they'd
                            arrived natively.
                        </li>
                        <li>
                            <strong className="text-ink">Desktop sync app</strong>{" "}
                            (better for continuous capture).{" "}
                            <a
                                href="https://www.amazon.com/clouddrive/getstarted"
                                target="_blank"
                                rel="noreferrer"
                                className="text-stamp-red underline hover:no-underline"
                            >
                                Install Amazon Photos for Desktop
                            </a>{" "}
                            → set it to sync down to a folder that's already
                            inside your Drive Watch tree. New photos appear in
                            Drive → Shed picks them up on the next 5-min poll.
                            No new credentials, no waiting on Amazon.
                        </li>
                    </ol>
                    <p className="text-[12px] text-ink-faint">
                        If Amazon ever re-opens the API, the Shed will get a
                        dedicated <code>Amazon Watch</code> ingestor mirroring
                        Drive / Gmail.
                    </p>
                </div>
            )}
        </div>
    );
}

function NotConfiguredCard() {
    return (
        <div className="paper-card border-2 border-stamp-red/40 p-6" data-testid="drive-not-configured">
            <div className="flex items-start gap-3">
                <Hourglass className="w-6 h-6 text-stamp-red mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div>
                    <div className="font-heading uppercase tracking-[0.18em] text-base text-ink mb-1">
                        Awaiting operator setup
                    </div>
                    <p className="font-body text-sm text-ink-soft leading-relaxed">
                        The Google OAuth client hasn't been configured yet. The operator must paste
                        <code className="px-1 mx-1 bg-paper-light border border-ink/20 text-[11px]">GOOGLE_CLIENT_ID</code>
                        and
                        <code className="px-1 mx-1 bg-paper-light border border-ink/20 text-[11px]">GOOGLE_CLIENT_SECRET</code>
                        into the backend environment, then restart the service.
                    </p>
                    <div className="label-typed text-[9px] mt-3 flex items-center gap-1.5">
                        <AlertCircle className="w-3 h-3" />
                        Setup guide is in Ern's last message.
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConnectCard({ onConnect }) {
    return (
        <div className="paper-card p-8 text-center max-w-2xl" data-testid="drive-connect">
            <Cloud className="w-12 h-12 text-stamp-red mx-auto mb-4" strokeWidth={1.2} />
            <div className="font-heading uppercase tracking-[0.2em] text-2xl text-ink mb-2">
                Connect your Google Drive
            </div>
            <p className="font-body text-sm text-ink-soft mb-6 leading-relaxed">
                Pick one folder in your Drive. The Shed will organise files dropped into its
                _Inbox sub-folder, rename them sensibly, and move duplicates aside. Originals
                stay in Drive, just better filed.
            </p>
            <button
                onClick={onConnect}
                data-testid="drive-connect-btn"
                className="inline-flex items-center gap-3 px-7 py-3 border-2 border-stamp-red bg-stamp-red text-paper font-heading uppercase tracking-[0.2em] text-sm hover:bg-paper hover:text-stamp-red transition-colors"
            >
                <Cloud className="w-4 h-4" />
                Authorise Google Drive
            </button>
            <div className="label-typed text-[9px] mt-5">
                Scope · read + move + rename in the chosen folder · revoke anytime via Google or Disconnect
            </div>
        </div>
    );
}

function PickFolderPromptCard({ email, onPick, onDisconnect }) {
    return (
        <div className="paper-card p-6 max-w-2xl" data-testid="drive-pick-folder">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="label-typed text-stamp-red flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3" />
                        Connected
                    </div>
                    <div className="font-heading uppercase tracking-[0.15em] text-sm text-ink mt-1">
                        {email || "Google account linked"}
                    </div>
                </div>
                <button
                    onClick={onDisconnect}
                    data-testid="drive-disconnect-btn"
                    className="text-xs font-body text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                >
                    <Unlink className="w-3 h-3" /> Disconnect
                </button>
            </div>
            <hr className="border-ink/10 my-4" />
            <div className="font-heading uppercase tracking-[0.18em] text-base text-ink mb-2">
                Pick the folder to watch
            </div>
            <p className="font-body text-sm text-ink-soft mb-5">
                Choose one folder in your Drive. Anything dropped in (now or in the future) gets
                shredded into the ledger.
            </p>
            <button
                onClick={onPick}
                data-testid="drive-open-picker-btn"
                className="inline-flex items-center gap-2 px-5 py-2 border-2 border-ink bg-paper text-ink font-heading uppercase tracking-[0.18em] text-xs hover:border-stamp-red hover:text-stamp-red"
            >
                <FolderOpen className="w-4 h-4" />
                Browse folders
            </button>
        </div>
    );
}

function WatchingCard({ status, polling, reorganising, onPollNow, onReorganise, onPickAgain, onDisconnect, onSaveSettings }) {
    const last = status.last_poll_result;
    const pollingEnabled = status.polling_enabled === true; // iter 31.60 — opt-in default false
    const interval = status.poll_interval_minutes || 60;
    const INTERVALS = [15, 30, 45, 60, 75, 90, 105, 120];
    const intervalLabel = (n) => (n === 60 ? "1h" : n === 120 ? "2h" : `${n}m`);
    // Iter 31.92 — synchronous diagnostic probe. Operator-facing
    // "why is nothing happening?" tool. Hits GET /oauth/drive/probe
    // and renders the diagnosis inline. No mutations.
    const [probing, setProbing] = useState(false);
    const [probe, setProbe] = useState(null);
    const runProbe = async () => {
        setProbing(true);
        setProbe(null);
        try {
            const { data } = await api.get("/oauth/drive/probe");
            setProbe(data);
        } catch (e) {
            setProbe({ error: e?.response?.data?.detail || e.message || "Probe failed" });
        } finally {
            setProbing(false);
        }
    };
    return (
        <div className="space-y-5" data-testid="drive-watching">
            <div className="paper-card border-2 border-ink/30 p-6 relative">
                <div className="absolute top-3 right-4 classified-stamp">WATCHING</div>
                <div className="flex items-start gap-3 mb-4">
                    <FolderOpen className="w-7 h-7 text-stamp-red flex-shrink-0" strokeWidth={1.4} />
                    <div className="flex-1">
                        <div className="label-typed text-[10px]">Watched folder · {status.google_email || "Drive"}</div>
                        <div className="font-heading text-2xl text-ink mt-1 tracking-[0.04em]">
                            {status.watched_folder_name || "(unnamed folder)"}
                        </div>
                        <div className="font-body text-[11px] text-ink-soft mt-1">
                            ID: <code>{status.watched_folder_id}</code>
                        </div>
                    </div>
                </div>

                {/* Structure (iter 28.1) — show the user the four subfolders the Shed manages */}
                <div className="border border-ink/15 bg-paper-light/50 p-3 mb-4" data-testid="drive-structure">
                    <div className="label-typed text-[9px] mb-2">Folder structure · auto-managed</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-body text-xs">
                        <Sub label="_Inbox" desc="Drop files here" />
                        <Sub label="_Filed" desc="Organised + renamed" />
                        <Sub label="_Duplicates" desc="Bytes-identical files" />
                        <Sub label="_Failed" desc="Re-drop after fix" />
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
                    <Stat label="Filed" value={status.files_ingested_total ?? 0} />
                    <Stat label="Duplicates" value={status.files_duplicates_total ?? 0} />
                    <Stat label="Last poll" value={status.last_poll_at ? new Date(status.last_poll_at).toLocaleString() : "—"} />
                    <Stat
                        label="Last result"
                        value={last ? `+${last.files_ingested || 0} new${last.files_duplicates ? ` · ${last.files_duplicates} dup` : ""}${last.files_failed ? ` · ${last.files_failed} failed` : ""}` : "Pending first poll"}
                    />
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-5">
                    <button
                        onClick={onPollNow}
                        disabled={polling}
                        data-testid="drive-poll-now-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-stamp-red text-stamp-red font-heading uppercase tracking-[0.18em] text-[11px] hover:bg-stamp-red hover:text-paper disabled:opacity-50"
                    >
                        {polling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                        {polling ? "Polling…" : "Poll now"}
                    </button>
                    <button
                        onClick={onPickAgain}
                        data-testid="drive-pick-again-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 text-ink font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red"
                    >
                        <FolderOpen className="w-3 h-3" />
                        Change folder
                    </button>
                    <button
                        onClick={onReorganise}
                        disabled={reorganising}
                        data-testid="drive-reorganise-btn"
                        title="Re-file every previously-filed item into the new <type>/YYYY-MM-DD schema. Dry-run first, then asks before moving."
                        className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 text-ink font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red disabled:opacity-50"
                    >
                        {reorganising ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderOpen className="w-3 h-3" />}
                        {reorganising ? "Reorganising…" : "Reorganise (one-shot)"}
                    </button>
                    <button
                        onClick={runProbe}
                        disabled={probing}
                        data-testid="drive-probe-btn"
                        title="Run a synchronous diagnostic — credentials, folder, _Inbox count, last poll result. Read-only."
                        className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 text-ink font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red disabled:opacity-50"
                    >
                        {probing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3" />}
                        {probing ? "Probing…" : "Run diagnostic"}
                    </button>
                    <button
                        onClick={onDisconnect}
                        data-testid="drive-disconnect-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-ink/30 text-ink-soft font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red hover:text-stamp-red ml-auto"
                    >
                        <Unlink className="w-3 h-3" /> Disconnect
                    </button>
                </div>
                <div className="label-typed text-[9px] mt-4">
                    {pollingEnabled
                        ? <>Auto-poll cadence · every <span className="text-stamp-red">{intervalLabel(interval)}</span> · drop files into <span className="text-stamp-red">_Inbox</span></>
                        : <><span className="text-stamp-red">Auto-poll disabled.</span> Drive Watch is dormant — only manual "Poll now" will fetch.</>
                    }
                </div>
                {probe && (
                    <div className="mt-4 border border-stamp-red/40 bg-stamp-red/5 p-4" data-testid="drive-probe-result">
                        <div className="label-typed text-[9px] mb-2 text-stamp-red">Diagnostic · {new Date().toLocaleTimeString()}</div>
                        {probe.error ? (
                            <div className="font-body text-sm text-stamp-red">Probe failed: {probe.error}</div>
                        ) : (
                            <>
                                <div className="font-body text-sm text-ink mb-3" data-testid="drive-probe-diagnosis">
                                    {probe.diagnosis || "—"}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 font-body text-xs">
                                    <div>Creds present · <span className={probe.creds_present ? "text-stamp-green" : "text-stamp-red"}>{String(probe.creds_present)}</span></div>
                                    <div>Creds valid · <span className={probe.creds_valid ? "text-stamp-green" : "text-stamp-red"}>{String(probe.creds_valid)}</span></div>
                                    <div>Folder pinned · <span className={probe.folder_pinned ? "text-stamp-green" : "text-stamp-red"}>{String(probe.folder_pinned)}</span></div>
                                    <div>_Inbox listable · <span className={probe.inbox_listable ? "text-stamp-green" : "text-stamp-red"}>{String(probe.inbox_listable)}</span></div>
                                    <div>_Inbox file count · <span className="text-ink">{probe.inbox_file_count}</span></div>
                                    <div>Auto-poll · <span className={probe.polling_enabled ? "text-stamp-green" : "text-ink-soft"}>{probe.polling_enabled ? "ON" : "OFF"}</span></div>
                                </div>
                                {probe.creds_error && (
                                    <div className="font-body text-xs text-stamp-red mt-2">Creds error: {probe.creds_error}</div>
                                )}
                                {probe.inbox_files_sample?.length > 0 && (
                                    <div className="mt-3">
                                        <div className="label-typed text-[9px] mb-1">_Inbox sample · first {probe.inbox_files_sample.length} of {probe.inbox_file_count}</div>
                                        <ul className="font-body text-xs text-ink-soft space-y-2" data-testid="drive-inbox-sample">
                                            {probe.inbox_files_sample.map((f, i) => (
                                                <InboxFileRow key={f.id || i} file={f} onTagged={runProbe} />
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {probe.last_poll_result && (
                                    <details className="mt-3">
                                        <summary className="font-body text-xs text-ink-soft cursor-pointer hover:text-stamp-red">Last poll result (raw)</summary>
                                        <pre className="text-[10px] text-ink-soft mt-2 overflow-x-auto">{JSON.stringify(probe.last_poll_result, null, 2)}</pre>
                                    </details>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Iter 31.23 — Per-user polling controls. On/off switch + 15-min increment dial. */}
            <div className="paper-card border border-ink/20 p-5" data-testid="drive-polling-controls">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="label-typed text-[10px] tracking-[0.2em]">Polling controls</div>
                        <div className="font-body text-[11px] text-ink-soft mt-1 max-w-md">
                            Iter 31.60 — auto-poll is now OPT-IN. Off by default after connecting a folder.
                            Switch it on to start background processing. Lower frequency = less LLM spend.
                        </div>
                        {!pollingEnabled && (
                            <div className="label-typed text-[10px] mt-2 text-stamp-red">
                                ⚠ Auto-poll is OFF — no new files will be processed until enabled.
                            </div>
                        )}
                    </div>

                    {/* On/off switch */}
                    <button
                        onClick={() => onSaveSettings({ polling_enabled: !pollingEnabled })}
                        data-testid="drive-polling-switch"
                        aria-pressed={pollingEnabled}
                        className={`inline-flex items-center gap-2 px-4 py-2 border-2 font-heading uppercase tracking-[0.18em] text-[11px] transition-colors ${
                            pollingEnabled
                                ? "border-stamp-green text-stamp-green hover:bg-stamp-green hover:text-paper"
                                : "border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                        }`}
                    >
                        <span
                            className={`inline-block w-2 h-2 rounded-full ${pollingEnabled ? "bg-stamp-green" : "bg-ink/30"}`}
                        />
                        Auto-poll · {pollingEnabled ? "ON" : "OFF"}
                    </button>
                </div>

                <div>
                    <div className="label-typed text-[10px] tracking-[0.2em] mb-2">
                        Frequency · {intervalLabel(interval)}{!pollingEnabled && " (paused)"}
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="drive-frequency-dial">
                        {INTERVALS.map((n) => {
                            const active = n === interval;
                            return (
                                <button
                                    key={n}
                                    onClick={() => onSaveSettings({ poll_interval_minutes: n })}
                                    disabled={!pollingEnabled}
                                    data-testid={`drive-freq-${n}`}
                                    className={`px-3 py-2 font-heading text-[11px] uppercase tracking-[0.12em] border-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                        active
                                            ? "border-stamp-red bg-stamp-red text-paper"
                                            : "border-ink/20 text-ink hover:border-stamp-red hover:text-stamp-red"
                                    }`}
                                >
                                    {intervalLabel(n)}
                                </button>
                            );
                        })}
                    </div>
                    <div className="label-typed text-[9px] mt-3 text-ink-faint">
                        Default hourly. Increments of 15 minutes from 15m to 2h. Setting saves on click.
                    </div>
                </div>
            </div>

            {last?.failed_samples?.length > 0 && (
                <div className="paper-card border border-stamp-red/40 p-5" data-testid="drive-last-failures">
                    <div className="font-heading uppercase tracking-[0.18em] text-xs text-stamp-red mb-2">
                        Last poll · {last.files_failed} file{last.files_failed === 1 ? "" : "s"} failed (moved to _Failed)
                    </div>
                    <ul className="space-y-1 font-body text-xs text-ink">
                        {last.failed_samples.map((f, i) => (
                            <li key={i} className="border-b border-ink/10 pb-1">
                                <span className="font-heading uppercase text-[10px] mr-2">{f.name}</span>
                                <span className="text-ink-soft">{f.error}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Sub({ label, desc }) {
    return (
        <div className="border border-ink/15 p-2">
            <div className="font-heading uppercase tracking-[0.12em] text-[10px] text-stamp-red">{label}</div>
            <div className="text-[10px] text-ink-soft mt-0.5">{desc}</div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="label-typed text-[9px]">{label}</div>
            <div className="font-heading text-base text-ink mt-1">{value}</div>
        </div>
    );
}

function FolderPickerModal({ folders, crumbs, query, onQuery, onDescend, onBack, onPin, onClose }) {
    // iter 30.3: when the user has descended into a folder, surface a clear
    // "Use this folder" CTA so they aren't stranded on "No folders here"
    // inside a leaf folder (which is the most common case — your data lives
    // in folders that contain FILES, not sub-folders).
    const currentFolder = crumbs.length > 0 ? crumbs[crumbs.length - 1] : null;
    return (
        <div
            className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            data-testid="drive-folder-modal"
        >
            <div className="paper-card border-2 border-ink/30 p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto relative">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="label-typed text-[9px]">Pick the watched folder</div>
                        <div className="font-heading uppercase tracking-[0.15em] text-base text-ink mt-1">
                            {crumbs.length ? crumbs.map(c => c.name).join(" / ") : "My Drive · root"}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-ink-soft hover:text-stamp-red text-xs font-body">
                        × Close
                    </button>
                </div>

                {currentFolder && (
                    <div
                        className="mb-4 p-4 border-2 border-stamp-red bg-paper-light"
                        data-testid="drive-pick-current"
                    >
                        <div className="label-typed text-[9px] mb-1">You are inside</div>
                        <div className="font-heading uppercase tracking-[0.12em] text-sm text-ink mb-3 break-all">
                            {currentFolder.name}
                        </div>
                        <button
                            onClick={() => onPin(currentFolder)}
                            className="w-full bg-ink text-paper-light font-heading uppercase tracking-[0.22em] text-sm py-3 hover:bg-stamp-red transition-colors"
                            data-testid="drive-pin-current"
                        >
                            Watch this folder
                        </button>
                        <div className="font-body text-[11px] text-ink-soft mt-2 leading-snug">
                            Drops anything into this folder (now or in future) into the ledger.
                            Sub-folders <span className="font-mono">_Inbox · _Filed · _Duplicates · _Failed</span> will be created inside it.
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                    {crumbs.length > 0 && (
                        <button
                            onClick={onBack}
                            className="inline-flex items-center gap-1 text-xs font-body text-ink hover:text-stamp-red"
                            data-testid="drive-folder-back"
                        >
                            <ArrowLeft className="w-3 h-3" /> Up
                        </button>
                    )}
                    <div className="relative flex-1">
                        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-ink-soft" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => onQuery(e.target.value)}
                            placeholder="Search sub-folders…"
                            data-testid="drive-folder-search"
                            className="w-full pl-7 pr-2 py-1.5 border border-ink/20 bg-paper-light font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                        />
                    </div>
                </div>

                {folders.length === 0 ? (
                    <div className="font-body text-sm text-ink-soft py-6 text-center border border-dashed border-ink/20" data-testid="drive-folder-picker-empty">
                        {query
                            ? `No folders match "${query}" anywhere in your Drive.`
                            : currentFolder
                            ? "No sub-folders inside. If this is where your files live, tap \"Watch this folder\" above."
                            : "No folders found at root. Type a folder name above to search your whole Drive."}
                    </div>
                ) : (
                    <>
                        <div className="label-typed text-[9px] mb-2">
                            Or go deeper — pick a sub-folder
                        </div>
                        <ul className="space-y-1">
                            {folders.map((f) => (
                                <li
                                    key={f.id}
                                    className="flex items-center justify-between border border-ink/15 p-3 hover:border-stamp-red"
                                    data-testid={`drive-folder-${f.id}`}
                                >
                                    <button
                                        onClick={() => onDescend(f)}
                                        className="flex items-center gap-2 flex-1 text-left font-body text-sm text-ink hover:text-stamp-red"
                                    >
                                        <FolderOpen className="w-3 h-3 text-ink-soft" />
                                        {f.name}
                                        <ChevronRight className="w-3 h-3 text-ink-soft ml-auto" />
                                    </button>
                                    <button
                                        onClick={() => onPin(f)}
                                        className="ml-2 inline-flex items-center gap-1 px-3 py-1 border border-stamp-red text-stamp-red font-heading uppercase tracking-[0.18em] text-[10px] hover:bg-stamp-red hover:text-paper"
                                        data-testid={`drive-pin-${f.id}`}
                                    >
                                        Pin
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
}
